import moment from 'moment-timezone';

import {
	RAW_REGULAR_SHIFT_FULL_ONE_ACTIVITY,
	RAW_REGULAR_SHIFT_FULL_TWO_SEQUENCES,
	RAW_REGULAR_THREE_DAYS_FULL_COVERAGE,
	RAW_SHORT_SHIFT_3H,
	RAW_SHORT_SHIFT_5H,
	RAW_SHORT_SHIFT_7H,
	RAW_SHORT_SHIFT_9H,
	RAW_THREE_DAYS_AUTOCLOSE_VARIANTS,
} from './crewTimelineRaw';

const RAW_MOCKS_BY_DRIVER = [
	RAW_REGULAR_SHIFT_FULL_ONE_ACTIVITY,
	RAW_REGULAR_SHIFT_FULL_TWO_SEQUENCES,
	RAW_REGULAR_THREE_DAYS_FULL_COVERAGE,
	RAW_THREE_DAYS_AUTOCLOSE_VARIANTS,
	RAW_SHORT_SHIFT_3H,
	RAW_SHORT_SHIFT_5H,
	RAW_SHORT_SHIFT_7H,
	RAW_SHORT_SHIFT_9H,
];

const STAR_WARS_DRIVERS = [
	{ fullName: 'Han Solo', driverCard: 'HSO1' },
	{ fullName: 'Luke Skywalker', driverCard: 'LSK1' },
	{ fullName: 'Leia Organa', driverCard: 'LOR1' },
	{ fullName: 'Chewbacca', driverCard: 'CHB1' },
	{ fullName: 'Taras Shortenko', driverCard: 'TSH1' },
	{ fullName: 'Antonio El`Des`Middles', driverCard: 'AEM1' },
	{ fullName: 'Tom Steadyshift JR', driverCard: 'TSJ1' },
	{ fullName: 'Tom Steadyshift', driverCard: 'TST1' },
];

const RAW_MOCK_ANCHOR_DAY = '2026-03-26';

const computeDayOffset = (anchorDate) => {
	const userTz = moment.tz.guess();
	const targetDay = moment.utc(anchorDate).tz(userTz).startOf('day');
	const rawAnchorDay = moment.tz(RAW_MOCK_ANCHOR_DAY, userTz).startOf('day');
	return targetDay.diff(rawAnchorDay, 'days');
};

const shiftTimestampByDays = (timestamp, dayOffset) => {
	if (!timestamp) return timestamp;

	const original = moment.utc(timestamp);
	if (!original.isValid()) return timestamp;

	return original.clone().add(dayOffset, 'days').toISOString();
};

const suffixId = (id, driverIndex) => (id ? `${id}__d${driverIndex}` : id);

const buildShiftForDriver = (shift, dayOffset, driverIndex) => ({
	...shift,
	id: suffixId(shift.id, driverIndex),
	fromTimestamp: shiftTimestampByDays(shift.fromTimestamp, dayOffset),
	toTimestamp: shiftTimestampByDays(shift.toTimestamp, dayOffset),
	autoCloseTimestamp: shift.autoCloseTimestamp
		? shiftTimestampByDays(shift.autoCloseTimestamp, dayOffset)
		: null,
});

const buildExtraForDriver = (extra, dayOffset, driverIndex) => ({
	...extra,
	id: suffixId(extra.id, driverIndex),
	sequenceGroupId: suffixId(extra.sequenceGroupId, driverIndex),
	fromTimestamp: shiftTimestampByDays(extra.fromTimestamp, dayOffset),
	toTimestamp: shiftTimestampByDays(extra.toTimestamp, dayOffset),
});

const buildActivityForDriver = (item, dayOffset, driverIndex) => {
	const activity = item.activity ?? {};
	const nextActivity = { ...activity };

	if (activity.shiftId) nextActivity.shiftId = suffixId(activity.shiftId, driverIndex);
	if (activity.rideId) nextActivity.rideId = suffixId(activity.rideId, driverIndex);
	if (activity.sequenceGroupId) {
		nextActivity.sequenceGroupId = suffixId(activity.sequenceGroupId, driverIndex);
	}
	if (activity.firstWorkAssignmentId) {
		nextActivity.firstWorkAssignmentId = suffixId(activity.firstWorkAssignmentId, driverIndex);
	}
	if (Array.isArray(activity.extraActivities)) {
		nextActivity.extraActivities = activity.extraActivities.map((extra) =>
			buildExtraForDriver(extra, dayOffset, driverIndex)
		);
	}

	return {
		...item,
		fromTimestamp: shiftTimestampByDays(item.fromTimestamp, dayOffset),
		toTimestamp: shiftTimestampByDays(item.toTimestamp, dayOffset),
		activity: nextActivity,
	};
};

const buildDriver = (driverIndex, rawMock, dayOffset) => {
	const profile = STAR_WARS_DRIVERS[driverIndex];

	return {
		id: `mock-driver-${driverIndex + 1}`,
		fullName: profile.fullName,
		driverCard: profile.driverCard,
		crewMember: {
			currentLocation: null,
			currentHomeBase: null,
			lastWorkAssignment: null,
			availabilityInfo: null,
			currentRun: null,
		},
		activitiesV2: (rawMock.activitiesV2 || []).map((item) =>
			buildActivityForDriver(item, dayOffset, driverIndex)
		),
		shifts: (rawMock.shifts || []).map((shift) =>
			buildShiftForDriver(shift, dayOffset, driverIndex)
		),
	};
};

export const buildMockedTimelineResults = (anchorDate) => {
	const dayOffset = computeDayOffset(anchorDate);

	return STAR_WARS_DRIVERS.map((_, index) => {
		const rawForDriver = RAW_MOCKS_BY_DRIVER[index];
		return buildDriver(index, rawForDriver, dayOffset);
	});
};
